import { localSync } from './LocalSyncService';
import { api } from './api';
import type {
  CardData,
  DisplayedWordHistoryItem,
  OutboundButton,
  SyncPreferencesRecord,
  SyncWordRecord,
  WordListItem,
  WordListResponse,
} from '../types';

export const ACTIVE_WORDS_NUMBER = 20;
export const DONT_REPEAT_WORDS_NUMBER = 10;

export function normalizeForComparison(text: string): string {
  if (!text) return '';
  return text
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[’‘`´]/g, "'")
    .replace(/[.,!?;:"«»„“”¡¿—\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function checkTranslationMatch(userInput: string, expectedText: string): boolean {
  const normUser = normalizeForComparison(userInput);
  if (!normUser) return false;

  const variants = expectedText
    .replace(/\([^)]*\)/g, ' ')
    .split(/[,;/|]+/)
    .map((v) => normalizeForComparison(v))
    .filter(Boolean);

  if (variants.length === 0) {
    return normUser === normalizeForComparison(expectedText);
  }
  return variants.some((v) => v === normUser);
}

export function selectTranscription(word: Pick<SyncWordRecord, 'phonetics_map' | 'language_code'>, preference: string): string {
  if (preference === '-') return '';
  const selected = word.phonetics_map?.[preference];
  if (selected) return selected;
  const ipa = word.phonetics_map?.ipa;
  return preference === 'ru_rs' && word.language_code === 'es' && ipa ? convertSpanishIpaToRu(ipa) : '';
}

export function convertSpanishIpaToRu(ipa: string): string {
  if (!ipa) return '';
  return ipa
    .replace(/tʃ/g, 'ч')
    .replace(/ɲ/g, 'нь')
    .replace(/ʎ/g, 'ль')
    .replace(/θ/g, 'с')
    .replace(/ð/g, 'д')
    .replace(/β/g, 'б')
    .replace(/ɣ/g, 'г')
    .replace(/x/g, 'х')
    .replace(/ɾ/g, 'р')
    .replace(/r/g, 'рр')
    .replace(/j/g, 'й')
    .replace(/w/g, 'у')
    .replace(/a/g, 'а')
    .replace(/e/g, 'э')
    .replace(/i/g, 'и')
    .replace(/o/g, 'о')
    .replace(/u/g, 'у')
    .replace(/b/g, 'б')
    .replace(/d/g, 'д')
    .replace(/f/g, 'ф')
    .replace(/g/g, 'г')
    .replace(/k/g, 'к')
    .replace(/l/g, 'л')
    .replace(/m/g, 'м')
    .replace(/n/g, 'н')
    .replace(/p/g, 'п')
    .replace(/s/g, 'с')
    .replace(/t/g, 'т');
}

export class LearningWorkflowEngine {
  /**
   * Globally reorders active words as `first, last, second, second-last, ...` by `word_id ASC`,
   * matching `DictionaryDatabase.get_n_active_words_with_total_shuffled` in Python.
   */
  public interleaveActiveWords(words: SyncWordRecord[]): SyncWordRecord[] {
    const sorted = [...words].sort((a, b) => a.id - b.id);
    const total = sorted.length;
    if (total <= 1) return sorted;

    const half = Math.floor((total + 1) / 2);
    const ranked = sorted.map((word, rn) => ({
      word,
      shuffleRank: rn < half ? rn * 2 : (total - 1 - rn) * 2 + 1,
    }));
    ranked.sort((a, b) => a.shuffleRank - b.shuffleRank);
    return ranked.map((r) => r.word);
  }

  /**
   * Returns eligible active words in the 20-word interleaved study pool (`ACTIVE_WORDS_NUMBER = 20`),
   * matching `DictionaryDatabase.get_n_active_words_with_total_shuffled` in Python.
   */
  public getActiveWindowWords(allWords: SyncWordRecord[], activeLang?: string): SyncWordRecord[] {
    const nowMs = Date.now();
    const isEligible = (w: SyncWordRecord) => {
      if (w.is_learned) return false;
      const stars = w.stars ?? 3;
      if (stars <= 0) return false;
      if (w.postponed_date) {
        const pTime = new Date(w.postponed_date).getTime();
        if (Number.isFinite(pTime) && pTime > nowMs) {
          return false;
        }
      }
      return true;
    };

    let filtered = allWords.filter(
      (w) => (!activeLang || w.language_code === activeLang) && isEligible(w)
    );
    if (filtered.length === 0 && activeLang) {
      // Fallback to any eligible word across user's dictionaries if selected language deck is empty
      filtered = allWords.filter(isEligible);
    }

    return this.interleaveActiveWords(filtered).slice(0, ACTIVE_WORDS_NUMBER);
  }

  /**
   * Selects the next flashcard from the 20-word interleaved pool, excluding the
   * 10-word non-repeat ring buffer (`displayed_words_list`), and picking uniformly
   * from the remaining candidates just like `LearnWords.py` in Telegram Bot.
   */
  public async selectNextCard(options?: {
    excludeWordId?: number;
    deterministicFirst?: boolean;
  }): Promise<{ card: CardData; buttons: OutboundButton[][] }> {
    const [allWords, prefs] = await Promise.all([
      localSync.getAllWords(),
      localSync.getPreferences(),
    ]);

    const activeLang =
      prefs.last_language ||
      prefs.selected_dictionaries?.[0] ||
      'ka';

    const windowWords = this.getActiveWindowWords(allWords, activeLang);
    if (windowWords.length === 0) {
      return {
        card: {
          word: '🎉 All active words in this deck are reviewed! Add new words with AI when online, or manage your dictionary below.',
          isInformational: true,
          rawText:
            '🎉 All active words in this deck are reviewed! Add new words with AI when online, or manage your dictionary below.',
        },
        buttons: [
          [
            { caption: '➕ Add Words', callback_data: 'add_words' },
            { caption: '📖 Dictionary', callback_data: 'open_dict' },
          ],
        ],
      };
    }

    const history: DisplayedWordHistoryItem[] = Array.isArray(prefs.displayed_words_list)
      ? prefs.displayed_words_list
      : [];
    const maxWordNumber = Math.min(DONT_REPEAT_WORDS_NUMBER, Math.max(0, windowWords.length - 1));
    const recentIds = new Set<number>(
      maxWordNumber > 0
        ? history.slice(-maxWordNumber).map((h) => Number(h.word_id))
        : []
    );
    if (options?.excludeWordId !== undefined) {
      recentIds.add(options.excludeWordId);
    }

    let candidateWords = windowWords.filter((w) => !recentIds.has(w.id));
    if (candidateWords.length === 0 && options?.excludeWordId !== undefined) {
      candidateWords = windowWords.filter((w) => w.id !== options.excludeWordId);
    }
    if (candidateWords.length === 0) {
      candidateWords = windowWords;
    }

    let chosen: SyncWordRecord;
    if (options?.deterministicFirst) {
      chosen = candidateWords[0];
    } else {
      const idx = Math.floor(Math.random() * candidateWords.length);
      chosen = candidateWords[idx] || candidateWords[0];
    }

    const userId = api.getSavedUser()?.user_id || '';
    const nextHistory: DisplayedWordHistoryItem[] = [
      ...history.filter((h) => Number(h.word_id) !== chosen.id),
      {
        user_id: userId,
        dictionary_id: chosen.dictionary_id,
        dict_id: chosen.dictionary_id,
        word_id: chosen.id,
        stars: chosen.stars,
      },
    ].slice(-DONT_REPEAT_WORDS_NUMBER);

    // Update local preferences history without blocking card presentation
    void localSync.savePreferences({ displayed_words_list: nextHistory });

    return {
      card: this.buildCardFromWord(chosen, prefs),
      buttons: this.buildButtonsForWord(chosen, prefs),
    };
  }

  public buildCardFromWord(word: SyncWordRecord, prefs: SyncPreferencesRecord): CardData {
    const targetLang = prefs.translation || 'en';
    const translation =
      word.translations?.[targetLang] ||
      word.translation ||
      word.translations?.en ||
      word.translations?.ru ||
      Object.values(word.translations || {})[0] ||
      '';

    const transcription = selectTranscription(word, prefs.transcription || 'ipa');

    const examples: Array<{ original: string; translation?: string }> = [];
    if (word.hint) {
      const lines = word.hint
        .split('\n')
        .map((l) => l.replace(/^\*{0,2}>+/, '').replace(/\|\|/g, '').trim())
        .filter((l) => l && !l.toLowerCase().includes('press to see'));
      for (const line of lines) {
        if (line.includes('—') || line.includes(' - ')) {
          const parts = line.split(/\s*[—\-]\s*/);
          examples.push({
            original: parts[0].trim(),
            translation: parts.slice(1).join(' — ').trim(),
          });
        } else {
          examples.push({ original: line });
        }
      }
    }

    const stars = Math.max(1, Math.min(5, word.stars || 3));
    const isTypingDrill = stars >= 5;
    const isReverse = stars === 4;
    const isSpoiler = stars === 3 || stars === 4;

    return {
      word: word.word,
      transcription,
      translation,
      examples,
      partOfSpeech: word.part_of_speech_name || undefined,
      isSpoiler,
      isRevealed: false,
      isInformational: false,
      level: stars,
      isReverse,
      isTypingDrill,
      wordId: word.id,
      dictionaryId: word.dictionary_id,
    };
  }

  public buildButtonsForWord(word: SyncWordRecord, prefs: SyncPreferencesRecord): OutboundButton[][] {
    const stars = word.stars || 3;
    const row: OutboundButton[] = [
      { caption: '➕ Add Words', callback_data: 'add_words' },
      { caption: '📖 Dictionary', callback_data: 'open_dict' },
    ];

    if (Array.isArray(prefs.selected_dictionaries) && prefs.selected_dictionaries.length > 1) {
      row.push({ caption: '🔄 Next Lang', callback_data: 'switch_lang' });
    }

    row.push({ caption: stars >= 5 ? '➡️ Skip' : '➡️ Next', callback_data: 'next' });

    return [row];
  }

  public async switchNextLanguage(): Promise<string> {
    const prefs = await localSync.getPreferences();
    const list = prefs.selected_dictionaries?.length ? prefs.selected_dictionaries : ['ka'];
    const currentIdx = list.indexOf(prefs.last_language);
    const nextLang = list[(currentIdx + 1) % list.length] || list[0] || 'ka';
    await localSync.savePreferences({ last_language: nextLang });
    return nextLang;
  }

  public async reactToWord(
    wordId: number,
    action: string,
    fallbackCard?: CardData | null
  ): Promise<SyncWordRecord | null> {
    const existing = await localSync.getWord(wordId);
    const currentStars = existing?.stars ?? fallbackCard?.level ?? 3;
    const cleanAction = action.trim().toLowerCase();

    let patch: Partial<Pick<SyncWordRecord, 'stars' | 'is_learned' | 'postponed_date'>> = {};

    if (cleanAction === 'star_up' || cleanAction === 'restore') {
      patch = {
        stars: cleanAction === 'restore' && currentStars === 0 ? 3 : Math.min(5, currentStars + 1),
        is_learned: false,
        postponed_date: null,
      };
    } else if (cleanAction === 'star_down') {
      const nextStars = Math.max(0, currentStars - 1);
      patch = {
        stars: nextStars,
        is_learned: false,
      };
    } else if (cleanAction === 'learned') {
      patch = {
        is_learned: true,
      };
    } else if (cleanAction === 'trash' || cleanAction === 'remove') {
      patch = {
        stars: 0,
        is_learned: false,
      };
    } else if (cleanAction === 'postpone') {
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      patch = {
        postponed_date: tomorrow,
      };
    } else {
      return existing;
    }

    return localSync.saveWordProgress(wordId, patch, {
      id: wordId,
      word_id: wordId,
      dictionary_id: fallbackCard?.dictionaryId || 1,
      word: fallbackCard?.word || '',
      translation: fallbackCard?.translation || '',
      phonetics: fallbackCard?.transcription || '',
      stars: currentStars,
    });
  }

  public async shiftPostponedDays(days: number): Promise<number> {
    const allWords = await localSync.getAllWords();
    const nowMs = Date.now();
    let shiftedCount = 0;

    for (const w of allWords) {
      if (!w.postponed_date) continue;
      const currentPostponedMs = new Date(w.postponed_date).getTime();
      if (!Number.isFinite(currentPostponedMs) || currentPostponedMs <= nowMs) continue;

      const nextMs = currentPostponedMs + days * 24 * 60 * 60 * 1000;
      const nextIso = nextMs <= nowMs ? null : new Date(nextMs).toISOString();
      await localSync.saveWordProgress(w.id, { postponed_date: nextIso }, w);
      shiftedCount += 1;
    }
    return shiftedCount;
  }

  public async queryWordList(params: {
    filter?: string;
    pos_id?: number | null;
    page?: number;
    limit?: number;
  }): Promise<WordListResponse> {
    const [allWords, prefs, catalogs] = await Promise.all([
      localSync.getAllWords(),
      localSync.getPreferences(),
      localSync.getCatalogs(),
    ]);

    const filter = (params.filter || 'active').trim().toLowerCase();
    const page = Math.max(0, params.page ?? 0);
    const limit = Math.max(1, params.limit ?? 15);
    const nowMs = Date.now();

    const partsOfSpeech = catalogs.parts_of_speech?.length
      ? catalogs.parts_of_speech
      : [
          { id: 1, name: 'Noun' },
          { id: 2, name: 'Verb' },
          { id: 3, name: 'Adjective' },
          { id: 4, name: 'Adverb' },
        ];

    const selectedPosId =
      params.pos_id !== undefined && params.pos_id !== null
        ? Number(params.pos_id)
        : partsOfSpeech[0]?.id ?? 1;

    const matching = allWords.filter((w) => {
      const isPostponedFuture =
        Boolean(w.postponed_date) && new Date(w.postponed_date!).getTime() > nowMs;
      const stars = w.stars ?? 3;

      if (filter === 'active') {
        return !w.is_learned && stars > 0 && !isPostponedFuture;
      }
      if (filter === 'learned') {
        return Boolean(w.is_learned);
      }
      if (filter === 'postponed') {
        return !w.is_learned && stars > 0 && isPostponedFuture;
      }
      if (filter === 'removed') {
        return stars === 0;
      }
      if (filter === 'part_of_speech' || filter === 'pos') {
        return stars > 0 && (w.part_of_speech_id ?? 1) === selectedPosId;
      }
      return true;
    });

    const ordered =
      filter === 'active'
        ? this.interleaveActiveWords(matching)
        : [...matching].sort((a, b) => a.id - b.id);
    const total = ordered.length;
    const slice = ordered.slice(page * limit, (page + 1) * limit);

    const targetLang = prefs.translation || 'en';
    const transPref = prefs.transcription || 'ipa';

    const items: WordListItem[] = slice.map((w) => ({
      word_id: w.id,
      word: w.word,
      stars: w.stars,
      translation:
        w.translations?.[targetLang] ||
        w.translation ||
        w.translations?.en ||
        Object.values(w.translations || {})[0] ||
        '',
      phonetics: selectTranscription(w, transPref),
      part_of_speech_id: w.part_of_speech_id ?? undefined,
      part_of_speech_name: w.part_of_speech_name ?? undefined,
      is_learned: w.is_learned,
      postponed_date: w.postponed_date,
      dictionary_id: w.dictionary_id,
    }));

    return {
      status: 'ok',
      filter,
      page,
      limit,
      total,
      selected_pos_id: selectedPosId,
      parts_of_speech: partsOfSpeech,
      items,
    };
  }
}

export const learningEngine = new LearningWorkflowEngine();
